import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr";
import { Japanese } from "flatpickr/dist/l10n/ja.js"
import { format, subDays } from 'date-fns'
import queryString from 'query-string'
import { mergeParams, getParams } from '../tools/h_params'
import { updateQueryStringParameter } from '../src/dashboard/helpers/url_helper'

require("flatpickr/dist/flatpickr.min.css")

export default class extends Controller {
  static values = {
    unlimitDateRange: {type: Boolean, default: false},
  }
  connect(){
    const input = this.element.querySelector('input')
    const maxDateRange = Number(this.element.dataset.maxDateRange) || 30
    const isUnlimitRange = this.unlimitDateRangeValue
    const maxDate = this.element.dataset.maxDate

    const noReload = String(this.element.dataset.noReload).toLowerCase() == "true"
    const isSecondDateRange = this.element.parentElement.id == 'compare-date'
    const allPresets = [
      {
        label: '昨日',
        days: 1,
        start: subDays(new Date(), 1),
        end: new Date(),
        isDefaultSelected: false
      },
      {
        label: '1週間',
        days: 7,
        start: subDays(new Date(), 7),
        end: new Date(),
        isDefaultSelected: false,
      },
      {
        label: '30日間',
        days: 30,
        start: subDays(new Date(), 30),
        end: new Date(),
        isDefaultSelected: false
      }
    ];
    const visiblePresets = isUnlimitRange
      ? allPresets
      : allPresets.filter(p => p.days <= maxDateRange);

    flatpickr(input, {
      mode: 'range',
      plugins: [
        predefinedRangesPlugin(visiblePresets)
      ],
      showMonths: 2,
      locale: Japanese,
      onChange(selectedDates, _dateStr, instance) {
        // MEMO: 30日の制限をする
        // https://github.com/flatpickr/flatpickr/issues/1489
        if(isUnlimitRange !== true){
          if(selectedDates.length === 1) {
            const newMinDate = selectedDates[0].fp_incr(-maxDateRange);
            const newMaxDate = selectedDates[0].fp_incr(maxDateRange);
            instance.set('minDate', newMinDate);
            instance.set('maxDate', newMaxDate);
          }
        }

        if(selectedDates.length === 2) {
          const newParams = {
            from_date: format(selectedDates[0], 'yyyy-MM-dd'),
            to_date: format(selectedDates[1], 'yyyy-MM-dd')
          }

          if(noReload !== true){
            location.href = `${location.pathname}?${queryString.stringify(mergeParams(newParams, true))}`
          }
          else {
            if(isSecondDateRange){
              window.history.replaceState({}, '', updateQueryStringParameter(window.location.href, 'from_date2', newParams.from_date));
              window.history.replaceState({}, '', updateQueryStringParameter(window.location.href, 'to_date2', newParams.to_date));
            }
            else {
              window.history.replaceState({}, '', updateQueryStringParameter(window.location.href, 'from_date1', newParams.from_date));
              window.history.replaceState({}, '', updateQueryStringParameter(window.location.href, 'to_date1', newParams.to_date));
            }
          }
        }
      },
      defaultDate: this.defaultDate(),
      maxDate: maxDate
    });
  }

  defaultDate() {
    const params = getParams()
    // dateFormat のデフォルトがY-m-dのためそのまま渡してOK
    if(params['from_date'] && params['to_date']) {
      return [params['from_date'], params['to_date']];
    }

    if(this.element.dataset.fromDate && this.element.dataset.toDate) {
      return [this.element.dataset.fromDate, this.element.dataset.toDate];
    }

    // defaultRange が明示的に空文字列 '' または 'false' の場合は初期値なし
    const defaultRangeAttr = this.element.dataset.defaultRange
    if (defaultRangeAttr === '' || defaultRangeAttr === 'false') {
      return null;
    }
    
    // defaultRange が指定されていれば、その日数を使う（例: "7" や "30"）
    const defaultRange = Number(this.element.dataset.defaultRange) || 30;
    return [subDays(new Date(), defaultRange), new Date()];
  }
}

// MEMO: 今日/30日間などの機能はこちらのissueコメントを採用している
// https://github.com/flatpickr/flatpickr/issues/1274#issuecomment-505008329

// vanilla js
/**
 * @typedef {Object} FlatpickrPluginRangePreset
 * @property {string} label
 * @property {number|Date|undefined} [start] Leave undefined to clear the date.
 * @property {number|Date} [end]
 * @property {boolean} [isDefaultSelected] Whether or not an option should be set as selected by default.
 */

const PLUGIN_CLASSNAME = 'flatpickr-calendar--with-predefined-ranges';

const BASE_CLASSNAME = 'flatpickr-predefined-ranges';
const RANGES_CONTAINER_CLASSNAME = `${BASE_CLASSNAME}__ranges-container`;
const BASE_OPTION_CLASSNAME = `${BASE_CLASSNAME}__ranges-container__option`;
const SELECTED_OPTION_CLASSNAME = `${BASE_OPTION_CLASSNAME}--selected`;

const MONTHS_AND_DATES_CONTAINER_CLASSNAME = `${BASE_CLASSNAME}__month-and-dates-container`;

/**
 * Adds a list of classes to an element, IE11 compliant.
 * @param {HTMLElement} elem
 * @param {...string} classes
 */
function addClassesToElem(elem, ...classes) {
  classes.forEach((className) => {
    elem.classList.add(className);
  });
}

/**
 * Removes a list of classes from an element, IE11 compliant.
 * @param {HTMLElement} elem
 * @param {...string} classes
 */
function removeClassesFromElem(elem, ...classes) {
  classes.forEach((className) => {
    elem.classList.remove(className);
  });
}

/**
 * @param {FlatpickrPluginRangePreset[]} presets
 * @param {string[]} [extraRangesContainerClasses=[]] a list of extra classes for the ranges container
 * @param {string[]} [extraRangeOptionClasses=[]] a list of extra classes for each range option.
 * @param {string[]} [extraRangeOptionSelectedClasses=[]] a list of extra classes for each range option when selected.
 * @returns {flatpickr.Options.Plugin}
 */
function predefinedRangesPlugin(presets, {
  extraRangesContainerClasses = [],
  extraRangeOptionClasses = [],
  extraRangeOptionSelectedClasses = [],
} = {}) {
  if (!presets || !Array.isArray(presets)) {
    throw new TypeError(`Presets must be an array, not ${presets}`);
  }

  if (presets.length === 0) {
    throw new Error('Must provide at least one preset.');
  }

  return (fp) => {
    /**
     * The element that contains the range options.
     * @type {HTMLElement}
     */
    let rangesContainer;
    let chosenOptionElem = null;

    /**
     * Clear the currently selected options, if one has been chosen.
     */
    const clearSelection = () => {
      if (chosenOptionElem !== null) {
        removeClassesFromElem(chosenOptionElem, SELECTED_OPTION_CLASSNAME, ...extraRangeOptionSelectedClasses);
        chosenOptionElem = null;
      }
    };

    /**
     * Set the currently selected option.
     * @param {HTMLElement} elem
     */
    const setSelection = (elem) => {
      chosenOptionElem = elem;
      addClassesToElem(chosenOptionElem, SELECTED_OPTION_CLASSNAME, ...extraRangeOptionSelectedClasses);
    };

    return {
      onReady() {
        rangesContainer = fp._createElement('div', RANGES_CONTAINER_CLASSNAME);
        addClassesToElem(rangesContainer, ...extraRangesContainerClasses);
        rangesContainer.tabIndex = -1;

        presets.forEach(({
          label, start, end, isDefaultSelected,
        }) => {
          const optionElem = fp._createElement('div', BASE_OPTION_CLASSNAME, label);
          addClassesToElem(optionElem, ...extraRangeOptionClasses);
          optionElem.tabIndex = 0;

          if (isDefaultSelected) {
            setSelection(optionElem);
          }
          optionElem.addEventListener('click', (event) => {
            // set the date range and trigger the change to handlers
            if (!start) {
              fp.clear();
            } else {
              fp.setDate([start, end], true);
            }
            fp.close();
            setSelection(event.target);
          });
          rangesContainer.appendChild(optionElem);
        });

        // Restructure the calendar to make it easier to style
        // placing the months and inner container into
        // one new sibling container to the ranges container
        // all wrapped in a base container
        // NOTE: if the calendarContainer has more than 1 child and it is not
        // vertically stacked then it will not calculate the correct position
        // for "above" positioning
        const monthAndDatesContainer = fp._createElement('div', MONTHS_AND_DATES_CONTAINER_CLASSNAME);
        monthAndDatesContainer.appendChild(fp.monthNav);
        monthAndDatesContainer.appendChild(fp.innerContainer);
        const baseContainer = fp._createElement('div', BASE_CLASSNAME);

        baseContainer.appendChild(monthAndDatesContainer);
        baseContainer.appendChild(rangesContainer);
        fp.calendarContainer.appendChild(baseContainer);
        addClassesToElem(fp.calendarContainer, PLUGIN_CLASSNAME);
      },
      onChange() {
        // Clear the selection when the user
        // chooses date on the calendar instead of a preset range
        clearSelection();
      },
    };
  };
}
